import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
  writeBatch,
  Timestamp,
  QueryConstraint
} from 'firebase/firestore';
import { db, auth } from '../config/firebase';
import {
  Equipo,
  HistorialEquipo,
  MantenimientoEquipo,
  FiltrosEquipos,
  MetricasEquipos,
  MetricasEquiposInventario,
  TipoEquipo,
  EstadoEquipo,
  TipoAccionEquipo,
  EstadoMantenimiento
} from '../types';
import { sanitizeForFirestore } from '../utils/firestoreHelpers';

/**
 * Interfaz para datos de equipo en entrega
 */
interface EquipoEntregaData {
  equipoId: string;
  tipo: TipoEquipo;
  marca: string;
  modelo: string;
  numeroSerie: string;
  accion: 'instalar' | 'retirar' | 'mantenimiento';
  observaciones?: string;
  costo?: number;
  ubicacion?: string;
  fechaAccion?: Date;
}

export class EquiposService {
  /**
   * Obtener la ruta del tenant del usuario actual
   */
  private static getTenantPath(collectionName: string): string {
    const user = auth.currentUser;
    if (!user?.email) {
      throw new Error('Usuario no autenticado');
    }
    return `tenants/${user.email}/${collectionName}`;
  }

  /**
   * Convertir Timestamps de Firestore a Date
   */
  private static convertTimestamps<T>(data: unknown): T {
    if (!data) return data as T;
    
    if (data instanceof Timestamp) {
      return data.toDate() as unknown as T;
    }
    
    if (Array.isArray(data)) {
      return data.map(item => this.convertTimestamps(item)) as unknown as T;
    }
    
    if (typeof data === 'object' && data !== null) {
      const converted: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(data)) {
        converted[key] = this.convertTimestamps(value);
      }
      return converted as T;
    }
    
    return data as T;
  }

  /**
   * Obtener todos los equipos
   */
  static async getEquipos(filtros?: FiltrosEquipos): Promise<Equipo[]> {
    try {
      const equiposRef = collection(db, this.getTenantPath('equipos'));
      
      // Construir consulta base
      const constraints: QueryConstraint[] = [];
      
      // Aplicar filtros
      if (filtros) {
        if (filtros.clienteId) {
          constraints.push(where('clienteId', '==', filtros.clienteId));
        }
        if (filtros.tipo) {
          constraints.push(where('tipo', '==', filtros.tipo));
        }
        if (filtros.estado) {
          constraints.push(where('estado', '==', filtros.estado));
        }
        if (filtros.marca) {
          constraints.push(where('marca', '==', filtros.marca));
        }
      }

      // Intentar con ordenamiento primero
      try {
        constraints.push(orderBy('fechaInstalacion', 'desc'));
        const q = query(equiposRef, ...constraints);
        const snapshot = await getDocs(q);
        const equipos = snapshot.docs.map(doc => ({
          id: doc.id,
          ...this.convertTimestamps<Omit<Equipo, 'id'>>(doc.data())
        }));

        // Aplicar filtros de búsqueda en memoria si es necesario
        if (filtros?.busqueda) {
          const busqueda = filtros.busqueda.toLowerCase();
          return equipos.filter(equipo =>
            equipo.marca?.toLowerCase().includes(busqueda) ||
            equipo.modelo.toLowerCase().includes(busqueda) ||
            equipo.numeroSerie.toLowerCase().includes(busqueda) ||
            equipo.ubicacion?.toLowerCase().includes(busqueda)
          );
        }

        return equipos;
      } catch {
        // Si falla por índice, hacer consulta sin ordenamiento y ordenar en memoria
        // console.warn('Índice no encontrado, ordenando en memoria');
        constraints.pop(); // Remover el orderBy
        const q = query(equiposRef, ...constraints);
        const snapshot = await getDocs(q);
        let equipos = snapshot.docs.map(doc => ({
          id: doc.id,
          ...this.convertTimestamps<Omit<Equipo, 'id'>>(doc.data())
        }));

        // Ordenar por fecha de instalación en memoria
        equipos = equipos.sort((a, b) => {
          const fechaA = a.fechaInstalacion ? new Date(a.fechaInstalacion).getTime() : 0;
          const fechaB = b.fechaInstalacion ? new Date(b.fechaInstalacion).getTime() : 0;
          return fechaB - fechaA;
        });

        // Aplicar filtros de búsqueda en memoria si es necesario
        if (filtros?.busqueda) {
          const busqueda = filtros.busqueda.toLowerCase();
          equipos = equipos.filter(equipo =>
            equipo.marca?.toLowerCase().includes(busqueda) ||
            equipo.modelo.toLowerCase().includes(busqueda) ||
            equipo.numeroSerie.toLowerCase().includes(busqueda) ||
            equipo.ubicacion?.toLowerCase().includes(busqueda)
          );
        }

        return equipos;
      }
    } catch (error) {
      console.error('Error al obtener equipos:', error);
      throw new Error('Error al obtener equipos');
    }
  }

  /**
   * Obtener un equipo por ID
   */
  static async getEquipo(equipoId: string): Promise<Equipo | null> {
    try {
      const equipoRef = doc(db, this.getTenantPath('equipos'), equipoId);
      const snapshot = await getDoc(equipoRef);
      
      if (!snapshot.exists()) {
        return null;
      }

      return {
        id: snapshot.id,
        ...this.convertTimestamps<Omit<Equipo, 'id'>>(snapshot.data())
      };
    } catch (error) {
      console.error('Error al obtener equipo:', error);
      throw new Error('Error al obtener equipo');
    }
  }

  /**
   * Crear un nuevo equipo
   */
  static async crearEquipo(equipoData: Omit<Equipo, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error('Usuario no autenticado');
      }

      const equiposRef = collection(db, this.getTenantPath('equipos'));
      const nuevoEquipo = {
        ...equipoData,
        createdBy: user.email,
        updatedBy: user.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(equiposRef, nuevoEquipo);

      // Crear entrada en el historial solo si hay clienteId
      if (equipoData.clienteId) {
        await this.registrarAccionEquipo(docRef.id, equipoData.clienteId, TipoAccionEquipo.INSTALACION, 
          `Instalación de ${equipoData.marca} ${equipoData.modelo}`, 
          equipoData.observaciones, equipoData.costoInstalacion);
      }

      return docRef.id;
    } catch (error) {
      console.error('Error al crear equipo:', error);
      throw new Error('Error al crear equipo');
    }
  }

  /**
   * Actualizar un equipo
   */
  static async actualizarEquipo(equipoId: string, updates: Partial<Equipo>): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error('Usuario no autenticado');
      }

      const equipoRef = doc(db, this.getTenantPath('equipos'), equipoId);
      
      // Sanitizar datos para Firestore
      const updateData = sanitizeForFirestore({
        ...updates,
        updatedBy: user.email,
        updatedAt: serverTimestamp()
      });
      
      await updateDoc(equipoRef, updateData as Record<string, unknown>);
    } catch (error) {
      console.error('Error al actualizar equipo:', error);
      throw new Error('Error al actualizar equipo');
    }
  }

  /**
   * Eliminar un equipo
   */
  static async eliminarEquipo(equipoId: string): Promise<void> {
    try {
      const equipoRef = doc(db, this.getTenantPath('equipos'), equipoId);
      await deleteDoc(equipoRef);
    } catch (error) {
      console.error('Error al eliminar equipo:', error);
      throw new Error('Error al eliminar equipo');
    }
  }

  /**
   * Obtener equipos de un cliente específico
   */
  static async getEquiposPorCliente(clienteId: string): Promise<Equipo[]> {
    try {
      const equiposRef = collection(db, this.getTenantPath('equipos'));
      
      // Primero intentar con ordenamiento
      try {
        const q = query(
          equiposRef,
          where('clienteId', '==', clienteId),
          orderBy('fechaInstalacion', 'desc')
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => ({
          id: doc.id,
          ...this.convertTimestamps<Omit<Equipo, 'id'>>(doc.data())
        }));
      } catch {
        // Si falla por índice, hacer consulta simple y ordenar en memoria
        // console.warn('Índice no encontrado, ordenando en memoria');
        const q = query(equiposRef, where('clienteId', '==', clienteId));
        const snapshot = await getDocs(q);
        const equipos = snapshot.docs.map(doc => ({
          id: doc.id,
          ...this.convertTimestamps<Omit<Equipo, 'id'>>(doc.data())
        }));
        
        // Ordenar por fecha de instalación en memoria
        return equipos.sort((a, b) => {
          const fechaA = a.fechaInstalacion ? new Date(a.fechaInstalacion).getTime() : 0;
          const fechaB = b.fechaInstalacion ? new Date(b.fechaInstalacion).getTime() : 0;
          return fechaB - fechaA;
        });
      }
    } catch (error) {
      console.error('Error al obtener equipos del cliente:', error);
      throw new Error('Error al obtener equipos del cliente');
    }
  }

  /**
   * Registrar una acción en el historial del equipo
   */
  static async registrarAccionEquipo(
    equipoId: string,
    clienteId: string,
    tipoAccion: TipoAccionEquipo,
    descripcion: string,
    observaciones?: string,
    costo?: number,
    tecnico?: string,
    fotos?: string[]
  ): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error('Usuario no autenticado');
      }

      const historialRef = collection(db, this.getTenantPath('historialEquipos'));
      const accionData: Record<string, unknown> = {
        equipoId,
        clienteId,
        tipoAccion,
        fecha: new Date(),
        descripcion,
        createdAt: new Date(),
        createdBy: user.email
      };

      // Solo agregar campos que no sean undefined
      if (observaciones !== undefined) accionData.observaciones = observaciones;
      if (costo !== undefined) accionData.costo = costo;
      if (tecnico !== undefined) accionData.tecnico = tecnico;
      if (fotos !== undefined) accionData.fotos = fotos;

      const docRef = await addDoc(historialRef, accionData);
      return docRef.id;
    } catch (error) {
      console.error('Error al registrar acción del equipo:', error);
      throw new Error('Error al registrar acción del equipo');
    }
  }

  /**
   * Obtener historial de un equipo
   */
  static async getHistorialEquipo(equipoId: string): Promise<HistorialEquipo[]> {
    try {
      const historialRef = collection(db, this.getTenantPath('historialEquipos'));
      const q = query(
        historialRef,
        where('equipoId', '==', equipoId),
        orderBy('fecha', 'desc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...this.convertTimestamps<Omit<HistorialEquipo, 'id'>>(doc.data())
      }));
    } catch (error) {
      console.error('Error al obtener historial del equipo:', error);
      throw new Error('Error al obtener historial del equipo');
    }
  }

  /**
   * Crear un mantenimiento programado
   */
  static async crearMantenimiento(mantenimientoData: Omit<MantenimientoEquipo, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error('Usuario no autenticado');
      }

      const mantenimientosRef = collection(db, this.getTenantPath('mantenimientosEquipos'));
      const nuevoMantenimiento = {
        ...mantenimientoData,
        createdBy: user.email,
        updatedBy: user.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const docRef = await addDoc(mantenimientosRef, nuevoMantenimiento);
      return docRef.id;
    } catch (error) {
      console.error('Error al crear mantenimiento:', error);
      throw new Error('Error al crear mantenimiento');
    }
  }

  /**
   * Obtener mantenimientos pendientes
   */
  static async getMantenimientosPendientes(): Promise<MantenimientoEquipo[]> {
    try {
      const mantenimientosRef = collection(db, this.getTenantPath('mantenimientosEquipos'));
      const q = query(
        mantenimientosRef,
        where('estado', 'in', [EstadoMantenimiento.PROGRAMADO, EstadoMantenimiento.VENCIDO]),
        orderBy('fechaProgramada', 'asc')
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...this.convertTimestamps<Omit<MantenimientoEquipo, 'id'>>(doc.data())
      }));
    } catch (error) {
      console.error('Error al obtener mantenimientos pendientes:', error);
      throw new Error('Error al obtener mantenimientos pendientes');
    }
  }

  /**
   * Obtener métricas de equipos
   */
  static async getMetricasEquipos(): Promise<MetricasEquipos> {
    try {
      const equipos = await this.getEquipos();
      const mantenimientos = await this.getMantenimientosPendientes();

      const equiposPorTipo: Record<TipoEquipo, number> = {
        [TipoEquipo.DISPENSER_NORMAL]: 0,
        [TipoEquipo.DISPENSER_FRIO_CALOR]: 0,
        [TipoEquipo.MAQUINA_AGUA]: 0,
        [TipoEquipo.PURIFICADOR]: 0,
        [TipoEquipo.ENFRIADOR]: 0,
        [TipoEquipo.OTRO]: 0
      };

      const equiposPorEstado: Record<EstadoEquipo, number> = {
        [EstadoEquipo.ACTIVO]: 0,
        [EstadoEquipo.MANTENIMIENTO]: 0,
        [EstadoEquipo.RETIRADO]: 0,
        [EstadoEquipo.REPARACION]: 0,
        [EstadoEquipo.GARANTIA]: 0
      };

      let costoTotalMantenimientos = 0;
      let equiposConGarantia = 0;

      equipos.forEach(equipo => {
        equiposPorTipo[equipo.tipo]++;
        equiposPorEstado[equipo.estado]++;
        
        if (equipo.garantiaHasta && equipo.garantiaHasta > new Date()) {
          equiposConGarantia++;
        }
      });

      mantenimientos.forEach(mantenimiento => {
        if (mantenimiento.costo) {
          costoTotalMantenimientos += mantenimiento.costo;
        }
      });

      return {
        totalEquipos: equipos.length,
        equiposActivos: equiposPorEstado[EstadoEquipo.ACTIVO],
        equiposEnMantenimiento: equiposPorEstado[EstadoEquipo.MANTENIMIENTO],
        equiposRetirados: equiposPorEstado[EstadoEquipo.RETIRADO],
        mantenimientosPendientes: mantenimientos.filter(m => m.estado === EstadoMantenimiento.PROGRAMADO).length,
        mantenimientosVencidos: mantenimientos.filter(m => m.estado === EstadoMantenimiento.VENCIDO).length,
        equiposPorTipo,
        equiposPorEstado,
        costoTotalMantenimientos,
        equiposConGarantia
      };
    } catch (error) {
      console.error('Error al obtener métricas de equipos:', error);
      throw new Error('Error al obtener métricas de equipos');
    }
  }

  /**
   * Obtener equipos que necesitan mantenimiento
   */
  static async getEquiposNecesitanMantenimiento(): Promise<Equipo[]> {
    try {
      const equipos = await this.getEquipos();
      const hoy = new Date();
      
      return equipos.filter(equipo => {
        if (equipo.estado !== EstadoEquipo.ACTIVO) return false;
        
        // Verificar si necesita mantenimiento basado en la fecha del próximo mantenimiento
        if (equipo.proximoMantenimiento) {
          return equipo.proximoMantenimiento <= hoy;
        }
        
        // Si no hay fecha de próximo mantenimiento, verificar si han pasado más de 6 meses desde la instalación
        if (equipo.fechaInstalacion) {
          const seisMesesAtras = new Date();
          seisMesesAtras.setMonth(seisMesesAtras.getMonth() - 6);
          
          return equipo.fechaInstalacion <= seisMesesAtras;
        }
        
        return false;
      });
    } catch (error) {
      console.error('Error al obtener equipos que necesitan mantenimiento:', error);
      throw new Error('Error al obtener equipos que necesitan mantenimiento');
    }
  }

  /**
   * Retirar un equipo (eliminar de la base de datos)
   */
  static async retirarEquipo(equipoId: string, motivo: string, observaciones?: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error('Usuario no autenticado');
      }

      const batch = writeBatch(db);

      // Obtener datos del equipo directamente desde Firestore
      const equipoRef = doc(db, this.getTenantPath('equipos'), equipoId);
      const equipoSnapshot = await getDoc(equipoRef);
      
      if (!equipoSnapshot.exists()) {
        // El equipo ya no existe, pero registramos el intento de retiro en historial
        console.warn('Equipo ya no existe, registrando retiro en historial');
        const historialRef = collection(db, this.getTenantPath('historialEquipos'));
        const accionData: Record<string, unknown> = {
          equipoId,
          clienteId: 'desconocido', // No podemos obtener el clienteId
          tipoAccion: TipoAccionEquipo.RETIRO,
          fecha: new Date(),
          descripcion: `Retiro de equipo (ya eliminado): ${motivo}`,
          createdAt: new Date(),
          createdBy: user.email
        };

        if (observaciones !== undefined && observaciones.trim() !== '') {
          accionData.observaciones = observaciones;
        }

        batch.set(doc(historialRef), accionData);
        await batch.commit();
        return;
      }

      const equipoData = equipoSnapshot.data();
      const clienteId = equipoData?.clienteId || 'desconocido';

      // Eliminar el equipo de la base de datos
      batch.delete(equipoRef);

      // Registrar en historial
      const historialRef = collection(db, this.getTenantPath('historialEquipos'));
      const accionData: Record<string, unknown> = {
        equipoId,
        clienteId,
        tipoAccion: TipoAccionEquipo.RETIRO,
        fecha: new Date(),
        descripcion: `Retiro de equipo: ${motivo}`,
        createdAt: new Date(),
        createdBy: user.email
      };

      // Solo agregar observaciones si no es undefined
      if (observaciones !== undefined && observaciones.trim() !== '') {
        accionData.observaciones = observaciones;
      }

      batch.set(doc(historialRef), accionData);

      await batch.commit();
    } catch (error) {
      console.error('Error al retirar equipo:', error);
      throw new Error('Error al retirar equipo');
    }
  }

  // ============================================================================
  // NUEVAS FUNCIONES PARA PROCESAMIENTO DE EQUIPOS EN ENTREGAS
  // ============================================================================

  /**
   * Procesar equipo en una entrega
   */
  static async procesarEquipoEntrega(
    equipoData: {
      equipoId: string;
      tipo: TipoEquipo;
      marca: string;
      modelo: string;
      numeroSerie: string;
      accion: 'instalar' | 'retirar' | 'mantenimiento';
      observaciones?: string;
      costo?: number;
      ubicacion?: string;
      fechaAccion?: Date;
    },
    clienteId: string,
    entregaId: string
  ): Promise<void> {
    try {
      // Validar acción antes de procesar
      await this.validarAccionEquipo(equipoData.equipoId, equipoData.accion);

      const fechaAccion = equipoData.fechaAccion || new Date();

      switch (equipoData.accion) {
        case 'instalar':
          await this.instalarEquipoEnEntrega(equipoData, clienteId, entregaId, fechaAccion);
          break;
        
        case 'retirar':
          await this.retirarEquipoEnEntrega(equipoData, clienteId, entregaId, fechaAccion);
          break;
        
        case 'mantenimiento':
          await this.mantenimientoEquipoEnEntrega(equipoData, clienteId, entregaId, fechaAccion);
          break;
        
        default:
          throw new Error(`Acción no válida: ${equipoData.accion}`);
      }

      // Registrar en historial
      await this.registrarHistorialEquipoEntrega(equipoData, clienteId, entregaId, fechaAccion);

    } catch (error) {
      console.error('Error procesando equipo en entrega:', error);
      throw error;
    }
  }

  /**
   * Validar si una acción es válida para un equipo
   */
  static async validarAccionEquipo(
    equipoId: string,
    accion: 'instalar' | 'retirar' | 'mantenimiento'
  ): Promise<boolean> {
    try {
      const equipo = await this.getEquipo(equipoId);
      
      if (!equipo) {
        if (accion === 'instalar') {
          // Es válido instalar un equipo que no existe (se creará)
          return true;
        }
        throw new Error('Equipo no encontrado');
      }

      // Validar según el estado actual del equipo
      switch (accion) {
        case 'instalar':
          if (equipo.estado === EstadoEquipo.ACTIVO) {
            throw new Error('El equipo ya está instalado y activo');
          }
          return true;
        
        case 'retirar':
          if (equipo.estado === EstadoEquipo.RETIRADO) {
            throw new Error('El equipo ya está retirado');
          }
          return true;
        
        case 'mantenimiento':
          if (equipo.estado === EstadoEquipo.RETIRADO) {
            throw new Error('No se puede realizar mantenimiento en un equipo retirado');
          }
          return true;
        
        default:
          return false;
      }
    } catch (error) {
      console.error('Error validando acción de equipo:', error);
      throw error;
    }
  }

  /**
   * Instalar equipo en una entrega
   */
  private static async instalarEquipoEnEntrega(
    equipoData: EquipoEntregaData,
    clienteId: string,
    entregaId: string,
    fechaAccion: Date
  ): Promise<void> {
    try {
      const equiposRef = collection(db, this.getTenantPath('equipos'));
      
      const equipoNuevo = {
        clienteId,
        tipo: equipoData.tipo,
        marca: equipoData.marca,
        modelo: equipoData.modelo,
        numeroSerie: equipoData.numeroSerie,
        estado: EstadoEquipo.ACTIVO,
        ubicacion: equipoData.ubicacion || 'Instalado en cliente',
        fechaInstalacion: fechaAccion,
        fechaUltimaAccion: fechaAccion,
        observaciones: equipoData.observaciones || 'Instalado en entrega',
        costo: equipoData.costo || 0,
        entregaId,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      if (equipoData.equipoId) {
        // Actualizar equipo existente
        const equipoRef = doc(db, this.getTenantPath('equipos'), equipoData.equipoId);
        await updateDoc(equipoRef, equipoNuevo);
      } else {
        // Crear nuevo equipo
        await addDoc(equiposRef, equipoNuevo);
      }

    } catch (error) {
      console.error('Error instalando equipo en entrega:', error);
      throw error;
    }
  }

  /**
   * Retirar equipo en una entrega
   */
  private static async retirarEquipoEnEntrega(
    equipoData: EquipoEntregaData,
    _clienteId: string,
    entregaId: string,
    fechaAccion: Date
  ): Promise<void> {
    try {
      if (!equipoData.equipoId) {
        throw new Error('No se puede retirar un equipo sin ID');
      }

      const equipoRef = doc(db, this.getTenantPath('equipos'), equipoData.equipoId);
      
      await updateDoc(equipoRef, {
        estado: EstadoEquipo.RETIRADO,
        fechaRetiro: fechaAccion,
        fechaUltimaAccion: fechaAccion,
        observaciones: equipoData.observaciones || 'Retirado en entrega',
        entregaId,
        updatedAt: new Date()
      });

    } catch (error) {
      console.error('Error retirando equipo en entrega:', error);
      throw error;
    }
  }

  /**
   * Realizar mantenimiento en equipo en una entrega
   */
  private static async mantenimientoEquipoEnEntrega(
    equipoData: EquipoEntregaData,
    clienteId: string,
    entregaId: string,
    fechaAccion: Date
  ): Promise<void> {
    try {
      if (!equipoData.equipoId) {
        throw new Error('No se puede realizar mantenimiento sin ID de equipo');
      }

      const equipoRef = doc(db, this.getTenantPath('equipos'), equipoData.equipoId);
      
      // Actualizar estado del equipo
      await updateDoc(equipoRef, {
        estado: EstadoEquipo.MANTENIMIENTO,
        fechaUltimaAccion: fechaAccion,
        observaciones: equipoData.observaciones || 'Mantenimiento realizado en entrega',
        entregaId,
        updatedAt: new Date()
      });

      // Crear registro de mantenimiento
      const mantenimientosRef = collection(db, this.getTenantPath('mantenimientosEquipos'));
      await addDoc(mantenimientosRef, {
        equipoId: equipoData.equipoId,
        clienteId,
        tipo: 'MANTENIMIENTO',
        descripcion: equipoData.observaciones || 'Mantenimiento realizado en entrega',
        costo: equipoData.costo || 0,
        fecha: fechaAccion,
        entregaId,
        usuario: 'sistema',
        createdAt: new Date()
      });

    } catch (error) {
      console.error('Error realizando mantenimiento en entrega:', error);
      throw error;
    }
  }

  /**
   * Registrar historial de equipo en entrega
   */
  private static async registrarHistorialEquipoEntrega(
    equipoData: EquipoEntregaData,
    clienteId: string,
    entregaId: string,
    fechaAccion: Date
  ): Promise<void> {
    try {
      const historialRef = collection(db, this.getTenantPath('historialEquipos'));
      
      await addDoc(historialRef, {
        equipoId: equipoData.equipoId,
        clienteId,
        entregaId,
        accion: equipoData.accion,
        tipo: equipoData.tipo,
        marca: equipoData.marca,
        modelo: equipoData.modelo,
        numeroSerie: equipoData.numeroSerie,
        observaciones: equipoData.observaciones,
        costo: equipoData.costo || 0,
        fecha: fechaAccion,
        usuario: 'sistema',
        createdAt: new Date()
      });

    } catch (error) {
      console.error('Error registrando historial de equipo:', error);
      throw error;
    }
  }

  /**
   * Rollback de equipo en entrega (en caso de error)
   */
  static async rollbackEquipoEntrega(
    equipoId: string,
    accionOriginal: 'instalar' | 'retirar' | 'mantenimiento',
    clienteId: string
  ): Promise<void> {
    try {
      const equipo = await this.getEquipo(equipoId);
      
      if (!equipo) {
        console.warn('Equipo no encontrado para rollback');
        return;
      }

      // Aplicar rollback según la acción original
      switch (accionOriginal) {
        case 'instalar':
          // Si se instaló, retirarlo
          await this.retirarEquipo(equipoId, 'Rollback de instalación', 'Rollback automático por error en entrega');
          break;
        
        case 'retirar':
          // Si se retiró, volver a activarlo
          await this.actualizarEquipo(equipoId, {
            estado: EstadoEquipo.ACTIVO,
            observaciones: 'Restaurado por rollback de entrega',
            updatedAt: new Date()
          });
          break;
        
        case 'mantenimiento':
          // Si se hizo mantenimiento, volver al estado anterior
          await this.actualizarEquipo(equipoId, {
            estado: EstadoEquipo.ACTIVO,
            observaciones: 'Estado restaurado por rollback de mantenimiento',
            updatedAt: new Date()
          });
          break;
      }

      // Registrar rollback en historial
      const historialRef = collection(db, this.getTenantPath('historialEquipos'));
      await addDoc(historialRef, {
        equipoId,
        clienteId,
        accion: 'rollback',
        tipo: equipo.tipo,
        marca: equipo.marca,
        modelo: equipo.modelo,
        numeroSerie: equipo.numeroSerie,
        observaciones: `Rollback de ${accionOriginal} por error en entrega`,
        fecha: new Date(),
        usuario: 'sistema',
        createdAt: new Date()
      });

    } catch (error) {
      console.error('Error en rollback de equipo:', error);
      throw error;
    }
  }

  /**
   * Verificar si un equipo puede ser procesado en una entrega
   */
  static async verificarDisponibilidadEquipo(
    equipoId: string,
    accion: 'instalar' | 'retirar' | 'mantenimiento'
  ): Promise<{ disponible: boolean; motivo?: string }> {
    try {
      const equipo = await this.getEquipo(equipoId);
      
      if (!equipo) {
        if (accion === 'instalar') {
          return { disponible: true };
        }
        return { disponible: false, motivo: 'Equipo no encontrado' };
      }

      // Verificar disponibilidad según estado actual
      switch (accion) {
        case 'instalar':
          if (equipo.estado === EstadoEquipo.ACTIVO) {
            return { disponible: false, motivo: 'El equipo ya está instalado y activo' };
          }
          return { disponible: true };
        
        case 'retirar':
          if (equipo.estado === EstadoEquipo.RETIRADO) {
            return { disponible: false, motivo: 'El equipo ya está retirado' };
          }
          return { disponible: true };
        
        case 'mantenimiento':
          if (equipo.estado === EstadoEquipo.RETIRADO) {
            return { disponible: false, motivo: 'No se puede realizar mantenimiento en un equipo retirado' };
          }
          return { disponible: true };
        
        default:
          return { disponible: false, motivo: 'Acción no válida' };
      }
    } catch (error) {
      console.error('Error verificando disponibilidad de equipo:', error);
      return { disponible: false, motivo: 'Error verificando disponibilidad' };
    }
  }

  // ============================================================================
  // MÉTODOS PARA INVENTARIO DE EQUIPOS
  // ============================================================================

  /**
   * Obtener equipos en inventario (sin asignar a clientes)
   */
  static async getEquiposInventario(): Promise<Equipo[]> {
    try {
      const equiposRef = collection(db, this.getTenantPath('equipos'));
      
      // Consulta simple sin índices compuestos - solo equipos sin clienteId
      const q = query(
        equiposRef,
        where('clienteId', '==', null)
      );
      
      const snapshot = await getDocs(q);
      const equipos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...this.convertTimestamps<Omit<Equipo, 'id'>>(doc.data())
      }));
      
      // Ordenar en memoria por fecha de creación
      return equipos.sort((a, b) => {
        const fechaA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const fechaB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return fechaB - fechaA;
      });
    } catch (error) {
      console.error('Error al obtener equipos en inventario:', error);
      throw new Error('Error al obtener equipos en inventario');
    }
  }

  /**
   * Obtener equipos disponibles para asignar
   */
  static async getEquiposDisponibles(): Promise<Equipo[]> {
    try {
      const equiposRef = collection(db, this.getTenantPath('equipos'));
      
      // Consulta simple sin índices compuestos - solo equipos sin clienteId
      const q = query(
        equiposRef,
        where('clienteId', '==', null)
      );
      
      const snapshot = await getDocs(q);
      const equipos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...this.convertTimestamps<Omit<Equipo, 'id'>>(doc.data())
      }));
      
      // Filtrar en memoria por estado ACTIVO y ordenar por fecha de creación
      return equipos
        .filter(equipo => equipo.estado === EstadoEquipo.ACTIVO)
        .sort((a, b) => {
          const fechaA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const fechaB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return fechaB - fechaA;
        });
    } catch (error) {
      console.error('Error al obtener equipos disponibles:', error);
      
      // Fallback: intentar obtener todos los equipos y filtrar en memoria
      try {
        const equiposRef = collection(db, this.getTenantPath('equipos'));
        const snapshot = await getDocs(equiposRef);
        const equipos = snapshot.docs.map(doc => ({
          id: doc.id,
          ...this.convertTimestamps<Omit<Equipo, 'id'>>(doc.data())
        }));
        
        // Filtrar en memoria por clienteId null y estado ACTIVO
        return equipos
          .filter(equipo => !equipo.clienteId && equipo.estado === EstadoEquipo.ACTIVO)
          .sort((a, b) => {
            const fechaA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const fechaB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return fechaB - fechaA;
          });
      } catch (fallbackError) {
        console.error('❌ Error en fallback también:', fallbackError);
        throw new Error('Error al obtener equipos disponibles');
      }
    }
  }

  /**
   * Crear equipo en inventario
   */
  static async crearEquipoInventario(equipoData: Omit<Equipo, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error('Usuario no autenticado');
      }

      const equiposRef = collection(db, this.getTenantPath('equipos'));
      
      // Validar que el número de serie sea único
      const qSerie = query(equiposRef, where('numeroSerie', '==', equipoData.numeroSerie));
      const snapshotSerie = await getDocs(qSerie);
      if (!snapshotSerie.empty) {
        throw new Error('Ya existe un equipo con ese número de serie');
      }

      const nuevoEquipo = {
        ...equipoData,
        // Garantizar valores por defecto para inventario
        estado: equipoData.estado || EstadoEquipo.ACTIVO, // Por defecto ACTIVO
        ubicacionInventario: equipoData.ubicacionInventario || 'Depósito', // Por defecto Depósito
        clienteId: null, // Equipo en inventario
        enInventario: true,
        fechaIngresoInventario: new Date(),
        createdBy: user.email,
        updatedBy: user.email,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      // Sanitizar datos para Firestore (convertir undefined a null)
      const nuevoEquipoSanitized = sanitizeForFirestore(nuevoEquipo);

      const docRef = await addDoc(equiposRef, nuevoEquipoSanitized as Record<string, unknown>);
      
      // Nota: No se registra en historial aquí porque el historial
      // está diseñado para acciones con clientes (instalación, retiro, etc.)
      // La creación se registra con createdAt y createdBy
      
      return docRef.id;
    } catch (error) {
      console.error('Error al crear equipo en inventario:', error);
      // Lanzar el error original para mejor debugging
      throw error;
    }
  }

  /**
   * Asignar equipo del inventario a un cliente
   */
  static async asignarEquipoACliente(equipoId: string, clienteId: string, ubicacion?: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error('Usuario no autenticado');
      }

      const equipoRef = doc(db, this.getTenantPath('equipos'), equipoId);
      
      // Sanitizar datos para Firestore
      const updateData = sanitizeForFirestore({
        clienteId,
        enInventario: false,
        fechaInstalacion: new Date(),
        ubicacion,
        updatedBy: user.email,
        updatedAt: serverTimestamp()
      });
      
      await updateDoc(equipoRef, updateData as Record<string, unknown>);

      // Registrar en historial
      await this.registrarAccionEquipo(equipoId, clienteId, TipoAccionEquipo.INSTALACION, 
        'Equipo asignado desde inventario', '', 0);
    } catch (error) {
      console.error('Error al asignar equipo a cliente:', error);
      throw new Error('Error al asignar equipo a cliente');
    }
  }

  /**
   * Retirar equipo de cliente y devolverlo al inventario
   */
  static async retirarEquipoDeCliente(equipoId: string, motivo?: string): Promise<void> {
    try {
      const user = auth.currentUser;
      if (!user?.email) {
        throw new Error('Usuario no autenticado');
      }

      const equipoRef = doc(db, this.getTenantPath('equipos'), equipoId);
      const equipoDoc = await getDoc(equipoRef);
      
      if (!equipoDoc.exists()) {
        throw new Error('Equipo no encontrado');
      }

      const equipo = equipoDoc.data();
      const clienteId = equipo.clienteId;

      // Sanitizar datos para Firestore
      const updateData = sanitizeForFirestore({
        clienteId: null,
        enInventario: true,
        estado: EstadoEquipo.RETIRADO,
        updatedBy: user.email,
        updatedAt: serverTimestamp()
      });

      await updateDoc(equipoRef, updateData as Record<string, unknown>);

      // Registrar en historial
      if (clienteId) {
        await this.registrarAccionEquipo(equipoId, clienteId, TipoAccionEquipo.RETIRO, 
          motivo || 'Equipo retirado y devuelto al inventario', '', 0);
      }
    } catch (error) {
      console.error('Error al retirar equipo de cliente:', error);
      throw new Error('Error al retirar equipo de cliente');
    }
  }

  /**
   * Obtener métricas del inventario de equipos
   */
  static async getMetricasEquiposInventario(): Promise<MetricasEquiposInventario> {
    try {
      const equiposRef = collection(db, this.getTenantPath('equipos'));
      const snapshot = await getDocs(equiposRef);
      
      const equipos = snapshot.docs.map(doc => ({
        id: doc.id,
        ...this.convertTimestamps<Omit<Equipo, 'id'>>(doc.data())
      }));

      const metricas: MetricasEquiposInventario = {
        totalEquipos: equipos.length,
        equiposEnInventario: equipos.filter(e => e.enInventario).length,
        equiposAsignados: equipos.filter(e => !e.enInventario).length,
        equiposPorTipo: {} as Record<TipoEquipo, number>,
        equiposPorEstado: {} as Record<EstadoEquipo, number>,
        equiposPorUbicacion: {},
        equiposDisponibles: equipos.filter(e => e.enInventario && e.estado === EstadoEquipo.ACTIVO).length,
        equiposEnMantenimiento: equipos.filter(e => e.estado === EstadoEquipo.MANTENIMIENTO).length,
        equiposRetirados: equipos.filter(e => e.estado === EstadoEquipo.RETIRADO).length
      };

      // Contar por tipo
      Object.values(TipoEquipo).forEach(tipo => {
        metricas.equiposPorTipo[tipo] = equipos.filter(e => e.tipo === tipo).length;
      });

      // Contar por estado
      Object.values(EstadoEquipo).forEach(estado => {
        metricas.equiposPorEstado[estado] = equipos.filter(e => e.estado === estado).length;
      });

      // Contar por ubicación
      equipos.forEach(equipo => {
        const ubicacion = equipo.ubicacionInventario || 'Sin ubicación';
        metricas.equiposPorUbicacion[ubicacion] = (metricas.equiposPorUbicacion[ubicacion] || 0) + 1;
      });

      return metricas;
    } catch (error) {
      console.error('Error al obtener métricas del inventario:', error);
      throw new Error('Error al obtener métricas del inventario');
    }
  }
}
